import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import Swal from "sweetalert2";
import {
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBTable,
} from "mdbreact";
import { GenericProducts } from "../../../../../constants";
import { axioKit, fullName } from "../../../../../services/utilities";
import {
  PURCHASE_URL,
  formatDate,
  poNumber,
} from "../../../laboratory/purchaseOrders/common";
import { STORAGE_TYPES } from "../../../laboratory/catalog/products/form";
import {
  breakdownOf,
  levelsOf,
  packSizeOf,
  packUnitOf,
} from "../../../laboratory/catalog/products/packagesEditor";
import DispenseModal from "./dispenseModal";
import BatchDetails from "./batchDetails";
import { REASONS, SCOPES } from "./common";
import { STOCK_STATUS, shelfOf, unitText } from "./units";

const PRODUCTS_URL = "commerce/merchandise/products";

const keyOf = (product, variant) =>
  `${product?._id || product}:${variant?._id || variant || ""}`;

const daysTo = (date) => (new Date(date) - new Date()) / 86400000;

const thisMonth = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
};

// thin progress bar under a quantity
const Bar = ({ value, total, color }) => (
  <div className="progress mt-1" style={{ height: 5, width: 110 }}>
    <div
      className={`progress-bar bg-${color}`}
      role="progressbar"
      style={{ width: `${total ? Math.min((value / total) * 100, 100) : 0}%` }}
    />
  </div>
);

const SummaryCard = ({ icon, label, value, hint, color, bg }) => (
  <div
    className="d-flex align-items-center rounded p-3"
    style={{ background: bg, flex: "1 1 200px", gap: 12 }}
  >
    <div
      className="d-flex align-items-center justify-content-center rounded"
      style={{ width: 44, height: 44, background: color, color: "#fff" }}
    >
      <MDBIcon icon={icon} size="lg" />
    </div>
    <div>
      <small style={{ color }}>{label}</small>
      <h4 className="mb-0 font-weight-bold" style={{ color }}>
        {value}
      </h4>
      <small className="text-muted">{hint}</small>
    </div>
  </div>
);

/**
 * Product inventory of the branch: every registered brand + variant with what
 * is in the branch (total), open (in use) and remaining (sealed), per batch.
 * What is dispensed here is recorded as a monthly expense.
 * `scope` ("reagents" | "supplies") limits it to that kind of product.
 */
export default function InventoryRecords({ scope }) {
  const kind = SCOPES[scope],
    { token, activePlatform } = useSelector(({ auth }) => auth),
    [products, setProducts] = useState([]),
    [stock, setStock] = useState([]),
    // per brand + variant: open packs (+ units left), sealed packs, pack size
    [shelves, setShelves] = useState({}),
    [orders, setOrders] = useState([]),
    [history, setHistory] = useState([]),
    [view, setView] = useState("inventory"),
    [month, setMonth] = useState(thisMonth),
    [search, setSearch] = useState(""),
    [category, setCategory] = useState(""),
    [status, setStatus] = useState(""),
    [storage, setStorage] = useState(""),
    [sort, setSort] = useState({ key: "product", asc: true }),
    [expanded, setExpanded] = useState({}),
    [dispenseKey, setDispenseKey] = useState(null), // null closed, "" no item, key = item
    [isLoading, setIsLoading] = useState(false),
    { addToast } = useToasts();

  const branch = activePlatform?.branchId;

  const notify = useCallback(
    (message, appearance = "error") =>
      addToast(message, { appearance, autoDismiss: true }),
    [addToast],
  );

  const load = useCallback(() => {
    if (!branch || !token) return Promise.resolve();
    setIsLoading(true);
    return Promise.all([
      axioKit.universal(`${PRODUCTS_URL}/browse`, token, { branch }),
      axioKit.universal(`${PURCHASE_URL}/stock`, token, { branch }),
      axioKit.universal(`${PURCHASE_URL}/tracking`, token, { branch }),
      axioKit.universal(`${PURCHASE_URL}/adjustments`, token, {
        branch,
        month,
      }),
      // the packs: open ones and what is left in them, sealed ones
      axioKit.universal(`${PURCHASE_URL}/shelf`, token, { branch }),
    ])
      .then(([_products, _stock, _tracking, _history, _shelf]) => {
        setProducts(_products?.payload || []);
        setStock(_stock?.payload || []);
        setShelves(
          (_shelf?.payload || []).reduce((result, entry) => {
            result[keyOf(entry.product, entry.variant)] = entry;
            return result;
          }, {}),
        );
        setOrders(_tracking?.payload || []);
        setHistory(_history?.payload || []);
      })
      .catch((error) => notify(error.message))
      .finally(() => setIsLoading(false));
  }, [branch, token, month, notify]);

  useEffect(() => {
    load();
  }, [load]);

  // batches still in the branch per brand + variant, with their P.O.
  const batchesByKey = useMemo(
    () =>
      orders.reduce((result, order) => {
        order.items.forEach((item) => {
          const key = keyOf(item.productId, item.variant);
          item.batches
            .filter(({ available }) => available > 0)
            .forEach((batch) =>
              (result[key] = result[key] || []).push({
                ...batch,
                po: poNumber(order),
                receivedAt: order.received,
              }),
            );
        });
        return result;
      }, {}),
    [orders],
  );

  // every registered brand + variant of a known generic, with its stock
  const rows = useMemo(
    () =>
      products
        .filter(({ pgid }) => GenericProducts.find(pgid))
        .filter(({ pgid }) => !kind || kind.includes(pgid))
        .flatMap((product) => {
          const generic = GenericProducts.find(product.pgid);
          const base = generic.uom === "test" ? "tests" : "pcs";
          const variants = product.variants?.length
            ? product.variants
            : [{ _id: "", name: "" }];
          return variants.map((variant) => {
            const key = keyOf(product._id, variant._id);
            const entry = stock.find(
              (s) => keyOf(s.product, s.variant) === key,
            ) || { onHand: 0, consumed: 0 };
            const registered = packSizeOf(variant, product);
            const shelfEntry = shelves[key] || {};
            // the pack size received, else the registered one
            const packQty = Number(shelfEntry.packSize || registered);
            const unit =
              packQty > 1 ? packUnitOf(variant, product) || "pack" : base;
            const batches = (batchesByKey[key] || [])
              .slice()
              .sort(
                (a, b) =>
                  (a.expiry ? new Date(a.expiry) : Infinity) -
                  (b.expiry ? new Date(b.expiry) : Infinity),
              )
              .map((batch) => ({ ...batch, shelf: shelfOf(batch, packQty) }));
            const onHand = Math.max(entry.onHand || 0, 0);
            // in units (pcs / tests), read from the packs:
            // total = in use (left in open packs) + remaining (sealed)
            const inUse =
              packQty > 1 ? Math.min(shelfEntry.openLeft || 0, onHand) : 0;
            const shelf = {
              total: onHand,
              inUse,
              remaining: onHand - inUse,
              openPacks: shelfEntry.open || 0,
              sealedPacks: shelfEntry.sealed || 0,
            };
            const reorderLevel = variant.reorderLevel;
            // the reorder level is in its unit (box, pack, pcs...), the largest by default
            const levels = levelsOf(variant, product);
            const reorderAt =
              levels.find(({ bu }) => bu === variant.reorderUnit) || levels[0];
            const reorderUnit = reorderAt?.bu || unit;
            const reorderBase =
              (Number(reorderLevel) || 0) *
              (reorderAt && reorderAt !== levels[0]
                ? reorderAt.qnty
                : Math.max(packQty, 1));
            const status =
              onHand <= 0
                ? "out"
                : reorderLevel !== undefined &&
                    reorderLevel !== null &&
                    onHand <= reorderBase
                  ? "low"
                  : "inStock";
            const expired = batches
              .filter(({ expiry }) => expiry && daysTo(expiry) < 0)
              .reduce((s, b) => s + b.available, 0);
            return {
              key,
              product,
              variant: variant._id ? variant : null,
              generic,
              name: [product.name, variant.name].filter(Boolean).join(" · "),
              onHand,
              base,
              packQty,
              // received with fewer units per pack than registered
              wrongPack: packQty < registered ? registered : 0,
              // breakdown levels (box > pack > pc)
              packages: levels,
              unit,
              shelf,
              batches,
              status,
              reorderLevel,
              reorderUnit,
              expired,
              storage: product.storage || "roomTemp",
              // dispense modal
              lots: batches,
            };
          });
        }),
    [products, stock, shelves, batchesByKey, kind],
  );

  const scopedHistory = useMemo(
    () => history.filter(({ pgid }) => !kind || kind.includes(pgid)),
    [history, kind],
  );

  const categories = useMemo(
    () =>
      [...new Set(rows.map(({ generic }) => generic.category))]
        .filter(Boolean)
        .sort(),
    [rows],
  );

  const visibleRows = useMemo(() => {
    const key = search.trim().toLowerCase();
    const list = rows
      .filter(({ generic }) => !category || generic.category === category)
      .filter((row) => !status || row.status === status)
      .filter((row) => !storage || row.storage === storage)
      .filter(
        ({ name, generic, batches }) =>
          !key ||
          [
            name,
            generic.name,
            generic.abbreviation,
            ...batches.map(({ lot }) => lot),
          ]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(key)),
      );
    const direction = sort.asc ? 1 : -1;
    return list.sort((a, b) =>
      sort.key === "total"
        ? (a.onHand - b.onHand) * direction
        : a.name.localeCompare(b.name) * direction,
    );
  }, [rows, search, category, status, storage, sort]);

  const visibleHistory = useMemo(() => {
    const key = search.trim().toLowerCase();
    return scopedHistory.filter(
      ({ product, variantName, remarks }) =>
        !key ||
        [product?.name, variantName, remarks]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(key)),
    );
  }, [scopedHistory, search]);

  const summary = useMemo(
    () => ({
      total: rows.length,
      inStock: rows.filter(({ onHand }) => onHand > 0).length,
      low: rows.filter(({ status: s }) => s === "low").length,
      out: rows.filter(({ status: s }) => s === "out").length,
      openLots: rows.reduce(
        (sum, { batches }) =>
          sum + batches.filter(({ shelf }) => shelf.open > 0).length,
        0,
      ),
    }),
    [rows],
  );

  const inStock = useMemo(
    () => rows.filter(({ onHand }) => onHand > 0),
    [rows],
  );

  const handleDispense = async (data) => {
    setIsLoading(true);
    try {
      const { success } = await axioKit.save(
        PURCHASE_URL,
        { ...data, branchId: branch },
        token,
        "adjust",
      );
      notify(success, "success");
      setDispenseKey(null);
      await load();
    } catch (error) {
      notify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // sets the Qty / unit of the variant and fixes what was received counted by 1
  const handlePackSize = async (row) => {
    const unit = packUnitOf(row.variant, row.product);
    const { value, isConfirmed } = await Swal.fire({
      title: "Set pack size",
      html: `<b>${row.name}</b><br/><small>How many ${row.base} are in one ${
        unit || "pack"
      }? Received ${unit || "pack"}s counted as 1 ${row.base} are fixed too.</small>`,
      input: "number",
      inputAttributes: { min: 2, step: 1 },
      inputPlaceholder: "e.g. 100",
      showCancelButton: true,
      confirmButtonText: "Save",
      inputValidator: (input) =>
        !(Number(input) >= 2) || !Number.isInteger(Number(input))
          ? "Enter a whole number of at least 2."
          : undefined,
    });
    if (!isConfirmed) return;
    setIsLoading(true);
    try {
      const { success } = await axioKit.update(
        PURCHASE_URL,
        {
          branchId: branch,
          product: row.product._id,
          variant: row.variant?._id,
          size: Number(value),
          unit,
          base: row.base,
        },
        token,
        "packSize",
      );
      notify(success, "success");
      await load();
    } catch (error) {
      notify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // beginning, added, total, used, expired and ending of the selected month
  const handlePrint = () =>
    window.open(
      `/printout/inventory?month=${month}${scope ? `&scope=${scope}` : ""}`,
      "MonthlyInventory",
      "top=100px,left=100px,width=1050px,height=750px",
    );

  const clearFilters = () => {
    setSearch("");
    setCategory("");
    setStatus("");
    setStorage("");
    setMonth(thisMonth());
  };

  const toggleSort = (key) =>
    setSort((prev) => ({ key, asc: prev.key === key ? !prev.asc : true }));

  const SortIcon = ({ field }) => (
    <MDBIcon
      icon={sort.key === field ? (sort.asc ? "sort-up" : "sort-down") : "sort"}
      className="ml-1 text-muted"
    />
  );

  return (
    <MDBCard narrow className="pb-3">
      <MDBCardBody>
        {/* header */}
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div className="d-flex align-items-center" style={{ gap: 12 }}>
            <MDBIcon
              icon={kind?.icon || "cubes"}
              size="2x"
              className="text-primary"
            />
            <div>
              <h4 className="mb-0 font-weight-bold">
                {kind?.title || "Product Inventory"}
              </h4>
              <small className="text-muted">
                {kind?.hint || "Supplies"} with batch details: total, opened (in
                use) and remaining. What is dispensed is recorded as a monthly
                expense.
              </small>
            </div>
          </div>
          <div className="d-flex" style={{ gap: 8 }}>
            <MDBBtn
              color="success"
              className="m-0"
              title="Monthly inventory of the selected month (DOH hard copy)"
              onClick={handlePrint}
            >
              <MDBIcon icon="print" className="mr-2" /> Print monthly
            </MDBBtn>
            <MDBBtn
              color="primary"
              className="m-0"
              disabled={!inStock.length}
              onClick={() => setDispenseKey("")}
            >
              <MDBIcon icon="plus" className="mr-2" /> Dispense{" "}
              {kind?.noun || "product"}
            </MDBBtn>
          </div>
        </div>

        {/* summary */}
        <div className="d-flex flex-wrap mb-3" style={{ gap: 12 }}>
          <SummaryCard
            icon="cube"
            label="Total Items"
            value={summary.total}
            hint="Registered brands / variants"
            color="#1976d2"
            bg="#eef5fd"
          />
          <SummaryCard
            icon="box"
            label="Items in Stock"
            value={summary.inStock}
            hint="With available quantity"
            color="#2e7d32"
            bg="#eef8ef"
          />
          <SummaryCard
            icon="exclamation"
            label="Low Stock"
            value={summary.low}
            hint="At or below reorder level"
            color="#ef8f00"
            bg="#fff7e6"
          />
          <SummaryCard
            icon="box-open"
            label="Out of Stock"
            value={summary.out}
            hint="No available quantity"
            color="#d32f2f"
            bg="#fdeeee"
          />
          <SummaryCard
            icon="layer-group"
            label="Active Open Lots"
            value={summary.openLots}
            hint="Batches currently in use"
            color="#6a3fc8"
            bg="#f3eefc"
          />
        </div>

        {/* filters */}
        <div
          className="d-flex flex-wrap align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div
            className="position-relative"
            style={{ flex: "1 1 260px", maxWidth: 380 }}
          >
            <MDBIcon
              icon="search"
              className="position-absolute text-muted"
              style={{ top: "50%", left: 12, transform: "translateY(-50%)" }}
            />
            <input
              className="form-control"
              style={{ paddingLeft: 36, paddingRight: 30 }}
              placeholder="Search product, brand, generic or lot number"
              value={search}
              onChange={({ target }) => setSearch(target.value)}
            />
            {search && (
              <button
                type="button"
                className="btn btn-link position-absolute p-0 m-0 text-muted"
                style={{
                  top: "50%",
                  right: 12,
                  transform: "translateY(-50%)",
                  boxShadow: "none",
                  lineHeight: 1,
                }}
                title="Clear"
                aria-label="Clear search"
                onClick={() => setSearch("")}
              >
                <MDBIcon icon="times" />
              </button>
            )}
          </div>
          <select
            className="browser-default custom-select"
            style={{ width: 180 }}
            value={category}
            onChange={({ target }) => setCategory(target.value)}
          >
            <option value="">All categories</option>
            {categories.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <select
            className="browser-default custom-select"
            style={{ width: 160 }}
            value={status}
            onChange={({ target }) => setStatus(target.value)}
          >
            <option value="">All status</option>
            {Object.entries(STOCK_STATUS).map(([key, { label }]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          <select
            className="browser-default custom-select"
            style={{ width: 190 }}
            value={storage}
            onChange={({ target }) => setStorage(target.value)}
          >
            <option value="">All storage types</option>
            {Object.entries(STORAGE_TYPES).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          <input
            type="month"
            className="form-control"
            style={{ width: 170 }}
            value={month}
            title="Month of the monthly expenses"
            onChange={({ target }) => target.value && setMonth(target.value)}
          />
          <MDBBtn color="light" className="m-0 px-3" onClick={clearFilters}>
            <MDBIcon icon="times" className="mr-1" /> Clear
          </MDBBtn>
          <MDBBtn
            color="primary"
            className="m-0 px-3"
            disabled={isLoading}
            onClick={load}
          >
            <MDBIcon icon="sync" spin={isLoading} className="mr-1" /> Refresh
          </MDBBtn>
        </div>

        {/* view */}
        <div className="btn-group mb-3" role="group" aria-label="View">
          {[
            ["inventory", "Inventory", "boxes", visibleRows.length],
            ["expenses", "Monthly Expenses", "receipt", scopedHistory.length],
          ].map(([key, label, icon, count]) => (
            <button
              key={key}
              type="button"
              className={`btn btn-sm m-0 z-depth-0 ${
                view === key ? "btn-primary" : "btn-outline-primary"
              }`}
              style={{ textTransform: "none" }}
              onClick={() => setView(key)}
            >
              <MDBIcon icon={icon} className="mr-1" />
              {label}
              <MDBBadge
                color={view === key ? "light" : "primary"}
                className="ml-2"
              >
                {count}
              </MDBBadge>
            </button>
          ))}
        </div>

        {view === "inventory" ? (
          <MDBTable hover responsive className="mb-0">
            <thead className="grey lighten-4">
              <tr className="small">
                <th style={{ width: 30 }} />
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSort("product")}
                >
                  Product <SortIcon field="product" />
                </th>
                <th>Category</th>
                <th>Storage</th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSort("total")}
                >
                  Total Stock <SortIcon field="total" />
                </th>
                <th>Open (In Use)</th>
                <th>Remaining</th>
                <th>Status</th>
                <th>Reorder Level</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!visibleRows.length && (
                <tr>
                  <td colSpan={10} className="text-center text-muted py-4">
                    {isLoading
                      ? "Loading..."
                      : `No ${kind?.noun || "product"} found.`}
                  </td>
                </tr>
              )}
              {visibleRows.map((row) => {
                const isOpen = !!expanded[row.key];
                const stockStatus = STOCK_STATUS[row.status];
                return (
                  <Fragment key={row.key}>
                    <tr className={row.expired > 0 ? "red lighten-5" : ""}>
                      <td className="align-middle">
                        <MDBIcon
                          icon={isOpen ? "chevron-up" : "chevron-down"}
                          className="text-muted"
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            setExpanded((prev) => ({
                              ...prev,
                              [row.key]: !prev[row.key],
                            }))
                          }
                        />
                      </td>
                      <td className="align-middle">
                        <small className="d-block text-danger font-weight-bold">
                          {row.generic.abbreviation}
                        </small>
                        <b>{row.name}</b>
                        <small className="d-block text-muted">
                          {row.generic.name}
                        </small>
                      </td>
                      <td className="align-middle">
                        <MDBBadge color="light" className="indigo-text">
                          {row.generic.category}
                        </MDBBadge>
                      </td>
                      <td className="align-middle small">
                        {STORAGE_TYPES[row.storage]}
                      </td>
                      <td className="align-middle">
                        {/* every quantity in units (pcs / tests), packs under it */}
                        <b>{row.shelf.total}</b> {row.base}
                        {/* broken down by the packaging levels of the variant */}
                        {row.packages?.length > 1 ? (
                          <small className="d-block text-muted">
                            {breakdownOf(row.shelf.total, row.packages)}
                          </small>
                        ) : (
                          row.packQty > 1 && (
                            <small className="d-block text-muted">
                              1 {row.unit} = {row.packQty}
                            </small>
                          )
                        )}
                        <Bar
                          value={row.shelf.total}
                          total={row.shelf.total}
                          color="primary"
                        />
                        {row.wrongPack > 0 && (
                          <small className="d-block text-danger">
                            received as {row.packQty} per pack, should be{" "}
                            {row.wrongPack}
                          </small>
                        )}
                        {/* counted by piece, or fewer per pack than registered */}
                        {(row.packQty <= 1 || row.wrongPack > 0) && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 m-0 mt-1 d-block"
                            style={{ textTransform: "none" }}
                            title="Set how many pcs are in one pack"
                            onClick={() => handlePackSize(row)}
                          >
                            <MDBIcon icon="box" className="mr-1" />
                            Set pack size
                          </button>
                        )}
                      </td>
                      <td className="align-middle">
                        {row.shelf.inUse} {row.base}
                        {row.packQty > 1 && (
                          <small className="d-block text-muted">
                            {unitText(row.shelf.openPacks, row.unit)} open
                          </small>
                        )}
                        <Bar
                          value={row.shelf.inUse}
                          total={row.shelf.total}
                          color="warning"
                        />
                      </td>
                      <td className="align-middle">
                        <b>{row.shelf.remaining}</b> {row.base}
                        {row.packQty > 1 && (
                          <small className="d-block text-muted">
                            {unitText(row.shelf.sealedPacks, row.unit)} sealed
                          </small>
                        )}
                        <Bar
                          value={row.shelf.remaining}
                          total={row.shelf.total}
                          color="success"
                        />
                      </td>
                      <td className="align-middle">
                        <MDBBadge color={stockStatus.color}>
                          {stockStatus.label}
                        </MDBBadge>
                        {row.expired > 0 && (
                          <MDBBadge color="danger" className="d-block mt-1">
                            {row.expired} {row.base} expired
                          </MDBBadge>
                        )}
                      </td>
                      <td className="align-middle small">
                        {row.reorderLevel !== undefined &&
                        row.reorderLevel !== null
                          ? unitText(row.reorderLevel, row.reorderUnit)
                          : "—"}
                      </td>
                      <td className="align-middle text-center text-nowrap">
                        <MDBBtn
                          size="sm"
                          color={isOpen ? "primary" : "light"}
                          className="m-0 px-2"
                          title="Batch / lot details"
                          onClick={() =>
                            setExpanded((prev) => ({
                              ...prev,
                              [row.key]: !prev[row.key],
                            }))
                          }
                        >
                          <MDBIcon icon="eye" />
                        </MDBBtn>
                        <MDBBtn
                          size="sm"
                          color="light"
                          className="my-0 mr-0 ml-1 px-2"
                          title="Dispense this item"
                          disabled={row.onHand <= 0}
                          onClick={() => setDispenseKey(row.key)}
                        >
                          <MDBIcon icon="dolly" />
                        </MDBBtn>
                      </td>
                    </tr>

                    {isOpen && (
                      <tr>
                        <td colSpan={10} className="grey lighten-5 px-4 py-3">
                          <BatchDetails row={row} onChanged={load} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </MDBTable>
        ) : (
          <MDBTable small hover responsive className="mb-0">
            <thead className="grey lighten-4">
              <tr>
                <th>Date</th>
                <th>Item</th>
                <th className="text-right">Qty</th>
                <th>Status</th>
                <th>Date opened</th>
                <th>Reason</th>
                <th>Remarks</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {!visibleHistory.length && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    {isLoading ? "Loading..." : "No monthly expense yet."}
                  </td>
                </tr>
              )}
              {visibleHistory.map((entry) => {
                const generic = GenericProducts.find(entry.pgid);
                const reason = REASONS[entry.reason] || REASONS.other;
                return (
                  <tr key={entry._id}>
                    <td className="small text-nowrap">
                      {formatDate(entry.createdAt)}
                    </td>
                    <td className="small">
                      {generic && (
                        <code className="mr-1">{generic.abbreviation}</code>
                      )}
                      {[entry.product?.name, entry.variantName]
                        .filter(Boolean)
                        .join(" · ")}
                    </td>
                    <td className="small text-right">
                      {entry.qty} {generic?.uom === "test" ? "tests" : "pcs"}
                    </td>
                    <td>
                      {entry.condition ? (
                        <MDBBadge
                          color={
                            entry.condition === "intact" ? "success" : "warning"
                          }
                        >
                          {entry.condition === "intact" ? "Intact" : "Opened"}
                        </MDBBadge>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="small text-nowrap">
                      {entry.opened ? formatDate(entry.opened) : "—"}
                    </td>
                    <td>
                      <MDBBadge color={reason.color}>{reason.label}</MDBBadge>
                    </td>
                    <td className="small">{entry.remarks || "—"}</td>
                    <td className="small">
                      {entry.userId?.fullName
                        ? fullName(entry.userId.fullName)
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </MDBTable>
        )}
      </MDBCardBody>

      <DispenseModal
        show={dispenseKey !== null}
        rows={inStock}
        initialKey={dispenseKey || ""}
        toggle={() => setDispenseKey(null)}
        onSubmit={handleDispense}
        isLoading={isLoading}
      />
    </MDBCard>
  );
}
